import { LitElement, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ref } from "lit/directives/ref.js";
import type { ChatSidebarBlock } from "../types/chat-types.ts";

type RichRuntimeRenderableBlock = Extract<
  ChatSidebarBlock,
  { type: "diagram-mermaid" | "chart-vega-lite" | "math-latex" }
>;

type RuntimeContext = "inline" | "sidebar";
type RuntimeStatus = "idle" | "loading" | "ready" | "error";

type RuntimeModuleSpecifier = "mermaid" | "katex" | "vega-embed";
type RuntimeImporter = (specifier: RuntimeModuleSpecifier) => Promise<unknown>;

type MermaidRenderResult = {
  svg: string;
  bindFunctions?: (element: Element) => void;
};

export type MermaidApi = {
  initialize: (config: Record<string, unknown>) => void;
  render: (id: string, text: string) => Promise<MermaidRenderResult>;
};

export type KatexRenderOptions = {
  displayMode?: boolean;
  output?: "html" | "mathml" | "htmlAndMathml";
  strict?: "ignore" | "warn" | "error";
  throwOnError?: boolean;
  trust?: boolean;
};

export type KatexApi = {
  render: (expression: string, element: Element, options?: KatexRenderOptions) => void;
};

export type VegaView = {
  finalize?: () => void;
};

export type VegaEmbedOptions = {
  actions?: boolean;
  mode?: "vega" | "vega-lite";
  renderer?: "svg" | "canvas";
};

export type VegaEmbedResult = {
  view?: VegaView;
};

export type VegaEmbedFn = (
  element: Element,
  spec: unknown,
  options?: VegaEmbedOptions,
) => Promise<VegaEmbedResult>;

const BLOCKED_VEGA_KEYS = new Set(["url", "href", "expr", "signal"]);
const initializedMermaidApis = new WeakSet<MermaidApi>();
let mermaidRenderCount = 0;

function importRuntimeModule(specifier: RuntimeModuleSpecifier): Promise<unknown> {
  if (globalThis.__openclawRichRuntimeImport) {
    return globalThis.__openclawRichRuntimeImport(specifier);
  }
  switch (specifier) {
    case "mermaid":
      return import("mermaid");
    case "katex":
      return import("katex");
    case "vega-embed":
      return import("vega-embed");
  }
}

function isRuntimeRenderableBlock(block: ChatSidebarBlock | null): block is RichRuntimeRenderableBlock {
  return Boolean(
    block &&
      (block.type === "diagram-mermaid" || block.type === "chart-vega-lite" || block.type === "math-latex"),
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function getRuntimeBlockTitle(block: RichRuntimeRenderableBlock): string {
  return block.label ??
    (block.type === "diagram-mermaid"
      ? "Mermaid"
      : block.type === "chart-vega-lite"
        ? "Vega-Lite"
        : "LaTeX");
}

function getRuntimeBlockSource(block: RichRuntimeRenderableBlock): string {
  return block.content;
}

function getRuntimeStatusLabel(block: RichRuntimeRenderableBlock): string {
  return `Rendering ${getRuntimeBlockTitle(block)}…`;
}

function describeRuntimeError(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }
  if (typeof error === "string" && error.trim()) {
    return error.trim();
  }
  return "Unknown renderer error.";
}

function resolveMermaidApi(moduleValue: unknown): MermaidApi | null {
  const candidate = isRecord(moduleValue) && isRecord(moduleValue.default) ? moduleValue.default : moduleValue;
  if (!isRecord(candidate)) {
    return null;
  }
  return typeof candidate.initialize === "function" && typeof candidate.render === "function"
    ? (candidate as unknown as MermaidApi)
    : null;
}

function resolveKatexApi(moduleValue: unknown): KatexApi | null {
  const candidate = isRecord(moduleValue) && isRecord(moduleValue.default) ? moduleValue.default : moduleValue;
  if (!isRecord(candidate)) {
    return null;
  }
  return typeof candidate.render === "function" ? (candidate as unknown as KatexApi) : null;
}

function resolveVegaEmbed(moduleValue: unknown): VegaEmbedFn | null {
  if (typeof moduleValue === "function") {
    return moduleValue as VegaEmbedFn;
  }
  if (isRecord(moduleValue) && typeof moduleValue.default === "function") {
    return moduleValue.default as VegaEmbedFn;
  }
  return null;
}

function findBlockedVegaField(value: unknown, path: string[] = []): string | null {
  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      const blocked = findBlockedVegaField(item, [...path, `[${index}]`]);
      if (blocked) {
        return blocked;
      }
    }
    return null;
  }
  if (!isRecord(value)) {
    return null;
  }
  for (const [key, nested] of Object.entries(value)) {
    const nextPath = [...path, key];
    if (BLOCKED_VEGA_KEYS.has(key.toLowerCase())) {
      return nextPath.join(".");
    }
    const blocked = findBlockedVegaField(nested, nextPath);
    if (blocked) {
      return blocked;
    }
  }
  return null;
}

function getVegaLiteSpec(block: Extract<ChatSidebarBlock, { type: "chart-vega-lite" }>): unknown {
  const parsed = block.spec ?? JSON.parse(block.content);
  if (!isRecord(parsed)) {
    throw new Error("Vega-Lite spec must be a JSON object.");
  }
  const blockedField = findBlockedVegaField(parsed);
  if (blockedField) {
    throw new Error(`Blocked Vega-Lite field: ${blockedField}`);
  }
  return parsed;
}

async function renderMermaidInto(stage: HTMLElement, source: string): Promise<void> {
  const moduleName = "mermaid";
  const mermaidModule = (await importRuntimeModule(moduleName)) as unknown;
  const mermaid = resolveMermaidApi(mermaidModule);
  if (!mermaid) {
    throw new Error("Mermaid runtime is unavailable.");
  }
  if (!initializedMermaidApis.has(mermaid)) {
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "strict",
      suppressErrorRendering: true,
    });
    initializedMermaidApis.add(mermaid);
  }
  const renderResult = await mermaid.render(`openclaw-mermaid-${++mermaidRenderCount}`, source);
  stage.innerHTML = renderResult.svg;
  renderResult.bindFunctions?.(stage);
}

async function renderKatexInto(
  stage: HTMLElement,
  block: Extract<ChatSidebarBlock, { type: "math-latex" }>,
): Promise<void> {
  const moduleName = "katex";
  const katexModule = (await importRuntimeModule(moduleName)) as unknown;
  const katex = resolveKatexApi(katexModule);
  if (!katex) {
    throw new Error("KaTeX runtime is unavailable.");
  }
  katex.render(block.content, stage, {
    displayMode: block.displayMode ?? true,
    output: "mathml",
    strict: "ignore",
    throwOnError: false,
    trust: false,
  });
}

async function renderVegaLiteInto(
  stage: HTMLElement,
  block: Extract<ChatSidebarBlock, { type: "chart-vega-lite" }>,
): Promise<VegaView | null> {
  const moduleName = "vega-embed";
  const vegaEmbedModule = (await importRuntimeModule(moduleName)) as unknown;
  const embed = resolveVegaEmbed(vegaEmbedModule);
  if (!embed) {
    throw new Error("Vega-Lite runtime is unavailable.");
  }
  const result = await embed(stage, getVegaLiteSpec(block), {
    actions: false,
    mode: "vega-lite",
    renderer: "svg",
  });
  return result.view ?? null;
}

@customElement("openclaw-rich-runtime-block")
export class OpenClawRichRuntimeBlock extends LitElement {
  @property({ attribute: false }) block: RichRuntimeRenderableBlock | null = null;
  @property() context: RuntimeContext = "inline";

  @state() private status: RuntimeStatus = "idle";
  @state() private errorMessage: string | null = null;

  private stage: HTMLElement | null = null;
  private renderVersion = 0;
  private vegaView: VegaView | null = null;

  createRenderRoot() {
    return this;
  }

  disconnectedCallback(): void {
    this.renderVersion += 1;
    this.finalizeVegaView();
    super.disconnectedCallback();
  }

  protected updated(changedProperties: PropertyValues<this>): void {
    if (changedProperties.has("block") && this.stage) {
      void this.renderRuntime();
    }
  }

  private finalizeVegaView() {
    this.vegaView?.finalize?.();
    this.vegaView = null;
  }

  private setStage = (value?: Element) => {
    const nextStage = value instanceof HTMLElement ? value : null;
    if (this.stage === nextStage) {
      return;
    }
    this.stage = nextStage;
  };

  private async renderRuntime() {
    if (!this.stage || !this.block) {
      return;
    }

    const currentStage = this.stage;
    const block = this.block;
    const version = ++this.renderVersion;
    this.finalizeVegaView();
    currentStage.replaceChildren();
    this.status = "loading";
    this.errorMessage = null;

    try {
      if (block.type === "diagram-mermaid") {
        await renderMermaidInto(currentStage, block.content);
      } else if (block.type === "chart-vega-lite") {
        this.vegaView = await renderVegaLiteInto(currentStage, block);
      } else {
        await renderKatexInto(currentStage, block);
      }

      if (!this.isConnected || version !== this.renderVersion) {
        this.finalizeVegaView();
        currentStage.replaceChildren();
        return;
      }

      this.status = "ready";
    } catch (error) {
      if (!this.isConnected || version !== this.renderVersion) {
        this.finalizeVegaView();
        currentStage.replaceChildren();
        return;
      }
      this.finalizeVegaView();
      currentStage.replaceChildren();
      this.status = "error";
      this.errorMessage = describeRuntimeError(error);
    }
  }

  render() {
    if (!this.block) {
      return nothing;
    }

    const title = getRuntimeBlockTitle(this.block);

    return html`
      <section class=${[
        "chat-rich-runtime",
        `chat-rich-runtime--${this.context}`,
        `chat-rich-runtime--${this.block.type}`,
        this.status === "error" ? "chat-rich-runtime--error" : "",
      ]
        .filter(Boolean)
        .join(" ")}>
        <div class="chat-rich-runtime__header">
          <div class="chat-rich-runtime__title">${title}</div>
          <div class="chat-rich-runtime__meta">
            ${this.status === "loading" ? getRuntimeStatusLabel(this.block) : "Interactive preview"}
          </div>
        </div>
        <div class="chat-rich-runtime__stage-wrap">
          <div class="chat-rich-runtime__stage" ${ref(this.setStage)}></div>
          ${this.status === "loading"
            ? html`<div class="chat-rich-runtime__overlay chat-rich-runtime__overlay--loading">${getRuntimeStatusLabel(this.block)}</div>`
            : nothing}
          ${this.status === "error"
            ? html`
                <div class="chat-rich-runtime__overlay chat-rich-runtime__overlay--error">
                  <div class="chat-rich-runtime__error-title">${title} render failed</div>
                  <div class="chat-rich-runtime__error-text">${this.errorMessage}</div>
                </div>
              `
            : nothing}
        </div>
        <details class="chat-rich-runtime__source" ?open=${this.status === "error"}>
          <summary class="chat-rich-runtime__source-summary">View source</summary>
          <pre class="chat-rich-runtime__source-code"><code>${getRuntimeBlockSource(this.block)}</code></pre>
        </details>
      </section>
    `;
  }
}

declare global {
  var __openclawRichRuntimeImport: RuntimeImporter | undefined;

  interface HTMLElementTagNameMap {
    "openclaw-rich-runtime-block": OpenClawRichRuntimeBlock;
  }
}
